import type { Cap, Role } from '@artmozaik/shared';

export type Me = {
  id: number; name: string; username: string; role: Role; caps: Cap[];
  /** mock ise panelin tepesinde kalıcı uyarı şeridi gösterilir. */
  parasutKip: 'mock' | 'real' | 'off';
  /** Muhasebe programının tarayıcı adresi (firma hesabı); yoksa null. */
  muhasebeAdres?: string | null;
};

const ACCESS = 'am_access';
const REFRESH = 'am_refresh';

export const tokens = {
  get access() { return localStorage.getItem(ACCESS); },
  get refresh() { return localStorage.getItem(REFRESH); },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS, access);
    localStorage.setItem(REFRESH, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
  },
};

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** Aynı anda gelen 401'lerde tek yenileme isteği atılsın. */
let yenileme: Promise<boolean> | null = null;

async function yenile(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    tokens.clear();
    return false;
  }
  const data = (await res.json()) as { accessToken: string; refreshToken: string };
  tokens.set(data.accessToken, data.refreshToken);
  return true;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const cagir = () =>
    fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(tokens.access ? { authorization: `Bearer ${tokens.access}` } : {}),
        ...init.headers,
      },
    });

  let res = await cagir();

  // Access süresi geçtiyse bir kez yenile ve tekrar dene.
  if (res.status === 401 && tokens.refresh) {
    yenileme ??= yenile().finally(() => { yenileme = null; });
    if (await yenileme) res = await cagir();
  }

  if (res.status === 204) return undefined as T;
  const gövde = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (gövde as { error?: string }).error ?? 'İstek başarısız');
  return gövde as T;
}

/**
 * Tutar. Kuruş varsa GÖSTERİLİR: eskiden hep tam liraya yuvarlanıyordu ve
 * ekranda ara toplam + KDV, genel toplamı 1 ₺ tutmuyor; "Kalan ₺4.069" yazan
 * alacağın gerçeği 4.069,45 oluyordu (görüneni yazan 0,45 ₺ açık bırakıyordu).
 */
export const formatTl = (kurus: number) => {
  const tam = Math.round(kurus) % 100 === 0;
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency', currency: 'TRY',
    minimumFractionDigits: tam ? 0 : 2, maximumFractionDigits: tam ? 0 : 2,
  }).format(kurus / 100);
};

/**
 * Tarih. Fatura/termin gibi YALNIZ GÜN taşıyan değerler UTC gece yarısı
 * saklanıyor; İstanbul'a çevrilince her biri "03:00" gösteriyordu. Gece
 * yarısıysa saat basılmaz. Yıl, bu yıl değilse eklenir.
 */
export const formatTarih = (iso: string) => {
  const d = new Date(iso);
  const yalnizGun = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  const buYil = d.getUTCFullYear() === new Date().getUTCFullYear();
  return new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric', month: 'long',
    ...(buYil ? {} : { year: 'numeric' }),
    ...(yalnizGun ? {} : { hour: '2-digit', minute: '2-digit' }),
    timeZone: yalnizGun ? 'UTC' : 'Europe/Istanbul',
  }).format(d);
};

/**
 * WhatsApp derin linki. Numara "0532 111 22 33" girilince wa.me/0532… oluyor
 * ve açılmıyordu; ülke kodu tamamlanır.
 */
export const waLink = (telefon: string | null | undefined, metin?: string): string | null => {
  if (!telefon) return null;
  let n = telefon.replace(/\D/g, '');
  if (n.startsWith('00')) n = n.slice(2);
  else if (n.startsWith('0')) n = `90${n.slice(1)}`;
  else if (n.length === 10 && n.startsWith('5')) n = `90${n}`;
  if (n.length < 10) return null;
  return `https://wa.me/${n}${metin ? `?text=${encodeURIComponent(metin)}` : ''}`;
};

/** Teknik sabitlerin ekran adları. */
export const CARI_TIP_AD: Record<string, string> = {
  BAYI: 'Bayi', MIMAR: 'Mimar', PROJE: 'Proje', PERAKENDE: 'Perakende', FASONCU: 'Fasoncu',
};
export const CARI_DURUM_AD: Record<string, string> = {
  AKTIF: 'Aktif', PASIF: 'Pasif', KARA_LISTE: 'Kara liste',
};

/** Muhasebe programının ekranda geçen adı — tek yerden. */
export const MUHASEBE_AD = 'Paraşüt';

const sayiTr = (n: number, ondalik: number) =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: ondalik, maximumFractionDigits: ondalik }).format(n);

/**
 * KISA tutar — gösterge kartı ve grafik ekseni için; kart dışına taşmaz.
 *   1 milyar ve üstü → "1,25 Mr ₺"   (Mr = milyar)
 *   1 milyon ve üstü → "12,8 Mn ₺"   (Mn = milyon)
 *   altı             → "458.300 ₺"   (tam lira, kuruşsuz)
 * `eksen` verilirse bin de kısalır ("458 B ₺"; B = bin) — yalnız grafik ekseninde.
 * Tam tutar için her zaman `formatTl` kullanın (ipucu ve alt satır).
 */
export const formatKisa = (kurus: number, secenek: { eksen?: boolean } = {}) => {
  const tl = kurus / 100;
  const m = Math.abs(tl);
  const kisalt = (bolen: number, ek: string) => {
    const v = tl / bolen;
    return `${sayiTr(v, Math.abs(v) >= 10 ? 1 : 2)} ${ek} ₺`;
  };
  if (m >= 999_999_500) return kisalt(1e9, 'Mr');
  if (m >= 999_999.5) return kisalt(1e6, 'Mn');
  if (secenek.eksen && m >= 1000) return `${sayiTr(tl / 1000, m >= 10_000 || tl % 1000 === 0 ? 0 : 1)} B ₺`;
  return `${sayiTr(tl, 0)} ₺`;
};

/** Adet/miktar: binlik ayraçlı. */
export const formatSayi = (n: number, ondalik = 0) => sayiTr(n, Number.isInteger(n) ? 0 : ondalik || 2);

/** "2026-10-03" → "3 Eki 2026" (saat dilimi kaydırması olmadan). */
export const formatGun = (gun: string | null | undefined, yil = true) => {
  if (!gun) return '—';
  return new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short', ...(yil ? { year: 'numeric' } : {}), timeZone: 'UTC' })
    .format(new Date(`${gun.slice(0, 10)}T00:00:00Z`));
};
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
/** "2026-10" → "Eki 26". */
export const formatAy = (ay: string) => `${AY_KISA[Number(ay.slice(5, 7)) - 1]} ${ay.slice(2, 4)}`;
